import { useNavigate } from "react-router-dom";
import { useMemo } from "react";
import { useTranslation } from "react-i18next";
import Modal from "../../../../../../components/base/Modal";
import type { Trailer, ThemeClasses } from "../types";
import {
  getStatusColor,
  getStatusLabel,
  getTypeLabel,
  getConditionLabel,
  getBrakeTypeLabel,
  getSuspensionTypeLabel,
} from "../utils";
import { useVehiclesRealtimeStore } from "../../../../../../store/admin/vehiclesRealtimeStore";
import { getTimeUntil, type TimeUntilTranslations } from "../../devices";
import {
  getTimeAgo,
  type TimeTranslations,
} from "../../../../../../utils/getDate";
import type { TFunction } from "i18next";
import { useDisableCRUD } from "../../../../../../store/admin/companiesStore";
import useIsMonitor from "../../../../../../hooks/useIsMonitor";
interface TrailerDetailsModalProps {
  trailer: Trailer;
  themeClasses: ThemeClasses;
  onClose: () => void;
  onEdit: (trailer: Trailer) => void;
  t: TFunction;
}

export function TrailerDetailsModal({
  trailer,
  themeClasses,
  onClose,
  onEdit,
  t,
}: TrailerDetailsModalProps) {
  const navigate = useNavigate();
  const { t: tLite } = useTranslation("lite");
  const setFocusedVehicleId = useVehiclesRealtimeStore(
    (state) => state.setFocusedVehicleId,
  );
  const setHighlightedVehicleId = useVehiclesRealtimeStore(
    (state) => state.setHighlightedVehicleId,
  );
  const disableCRUD = useDisableCRUD();
  const hasVehicle = !!trailer.vehicle_id && trailer.vehicle_id !== "N/A";
  const { isMonitor, isReadOnly } = useIsMonitor();

  const timeTranslations: TimeTranslations = useMemo(
    () => ({
      noData: tLite("time.noData"),
      rightNow: tLite("time.rightNow"),
      min: tLite("time.min"),
      hour: tLite("time.hour"),
      hours: tLite("time.hours"),
      day: tLite("time.day"),
      days: tLite("time.days"),
      month: tLite("time.month"),
      months: tLite("time.months"),
      and: tLite("time.and"),
    }),
    [tLite],
  );

  const timeUntilTranslations: TimeUntilTranslations = useMemo(
    () => ({
      alreadyPassed: tLite("time.alreadyPassed"),
      inDays: tLite("time.inDays"),
      inDaysPlural: tLite("time.inDaysPlural"),
      inHours: tLite("time.inHours"),
      inHoursPlural: tLite("time.inHoursPlural"),
      inMinutes: tLite("time.inMinutes"),
      inMinutesPlural: tLite("time.inMinutesPlural"),
      inSeconds: tLite("time.inSeconds"),
    }),
    [tLite],
  );
  const handleViewOnMap = () => {
    if (!hasVehicle || !trailer.vehicle_id) return;
    setFocusedVehicleId(trailer.vehicle_id);
    setHighlightedVehicleId(trailer.vehicle_id, 5000);
    navigate("/map");
    onClose();
  };

  const labelClass = `text-xs ${themeClasses.textSecondary} mb-1`;
  const valueClass = `text-sm ${themeClasses.text} font-semibold`;

  return (
    <Modal
      title={t("trailers.detailsModal.title")}
      onClose={onClose}
      themeClasses={themeClasses}
      showEditButton={!isMonitor && !isReadOnly}
      onEdit={() => onEdit(trailer)}
      disableCRUD={disableCRUD}
      width="w-full max-w-[600px]"
      content={
        <div className="space-y-4 max-h-[70vh] overflow-y-auto px-2">
          {/* Header con tipo y estado */}
          <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-2">
            <div className="flex flex-col min-w-0">
              <h3 className={`font-semibold ${themeClasses.text} text-lg truncate`}>
                {getTypeLabel(trailer.type || "")}
              </h3>
              <p className={`text-sm ${themeClasses.textSecondary}`}>
                {trailer.brand} {trailer.year} • {trailer.plate}
              </p>
            </div>
            <div className="flex items-center gap-3 flex-shrink-0">
              <span
                className={`px-3 py-1 rounded-full text-sm font-medium ${getStatusColor(trailer.status || "")}`}
              >
                {getStatusLabel(trailer.status || "", t)}
              </span>
              {hasVehicle && (
                <div className="text-right">
                  <p className={`text-xs ${themeClasses.textSecondary}`}>
                    Camión asignado
                  </p>
                  <p className={`text-sm ${themeClasses.text} font-bold`}>
                    {trailer.vehicle?.name}
                  </p>
                </div>
              )}
            </div>
          </div>

          {/* Image */}
          <div className="flex justify-center items-center border rounded-lg">
            <img
              src={trailer.image}
              alt={trailer.name}
              className="w-full max-h-64 object-cover"
            />
          </div>
          {/* Información Básica */}
          <h4
            className={`text-sm font-semibold ${themeClasses.text} ${themeClasses.cardBorder}`}
          >
            {t("trailers.sections.basicInfo")}
          </h4>

          <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
            <div>
              <p className={labelClass}>{t("trailers.typeTitle")}</p>
              <p className={valueClass}>
                {getTypeLabel(trailer.type || "") || "N/A"}
              </p>
            </div>
            <div>
              <p className={labelClass}>{t("trailers.fields.brand")}</p>
              <p className={valueClass}>{trailer.brand || "N/A"}</p>
            </div>
            <div>
              <p className={labelClass}>{t("trailers.fields.year")}</p>
              <p className={valueClass}>{trailer.year || "N/A"}</p>
            </div>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
            <div>
              <p className={labelClass}>{t("trailers.fields.plate")}</p>
              <p className={valueClass}>{trailer.plate || "N/A"}</p>
            </div>
            <div>
              <p className={labelClass}>{t("trailers.fields.vin")}</p>
              <p className={valueClass}>{trailer.vin || "N/A"}</p>
            </div>
            <div>
              <p className={labelClass}>{t("trailers.fields.serialNumber")}</p>
              <p className={valueClass}>{trailer.serialNumber || "N/A"}</p>
            </div>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <div>
              <p className={labelClass}>{t("trailers.fields.status")}</p>
              <p className={valueClass}>
                {getStatusLabel(trailer.status || "") || "N/A"}
              </p>
            </div>
            <div>
              <p className={labelClass}>{t("trailers.fields.condition")}</p>
              <p className={valueClass}>
                {getConditionLabel(trailer.condition || "") || "N/A"}
              </p>
            </div>
          </div>

          {/* Especificaciones */}
          <h4
            className={`text-sm font-semibold ${themeClasses.text} ${themeClasses.cardBorder} mt-4`}
          >
            {t("trailers.sections.specifications")}
          </h4>

          <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
            <div>
              <p className={labelClass}>{t("trailers.fields.capacity")}</p>
              <p className={valueClass}>
                {trailer.capacity ? `${trailer.capacity}` : "N/A"}
              </p>
            </div>
            <div>
              <p className={labelClass}>{t("trailers.fields.maximumWeight")}</p>
              <p className={valueClass}>
                {trailer.maximumWeight ? `${trailer.maximumWeight}` : "N/A"}
              </p>
            </div>
            <div>
              <p className={labelClass}>{t("trailers.fields.axles")}</p>
              <p className={valueClass}>{trailer.axles || "N/A"}</p>
            </div>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
            <div>
              <p className={labelClass}>{t("trailers.fields.length")}</p>
              <p className={valueClass}>
                {trailer.length ? `${trailer.length} ft` : "N/A"}
              </p>
            </div>
            <div>
              <p className={labelClass}>{t("trailers.fields.width")}</p>
              <p className={valueClass}>
                {trailer.width ? `${trailer.width} ft` : "N/A"}
              </p>
            </div>
            <div>
              <p className={labelClass}>{t("trailers.fields.height")}</p>
              <p className={valueClass}>
                {trailer.height ? `${trailer.height} ft` : "N/A"}
              </p>
            </div>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <div>
              <p className={labelClass}>{t("trailers.fields.brakeType")}</p>
              <p className={valueClass}>
                {getBrakeTypeLabel(trailer.brakeType || "") || "N/A"}
              </p>
            </div>
            <div>
              <p className={labelClass}>
                {t("trailers.fields.suspensionType")}
              </p>
              <p className={valueClass}>
                {getSuspensionTypeLabel(trailer.suspensionType || "") || "N/A"}
              </p>
            </div>
          </div>

          {/* Documentos */}
          <h4
            className={`text-sm font-semibold ${themeClasses.text} ${themeClasses.cardBorder} mt-4`}
          >
            {t("trailers.sections.documents")}
          </h4>

          <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
            <div>
              <p className={labelClass}>{t("trailers.fields.registration")}</p>
              <p className={valueClass}>{trailer.registration || "N/A"}</p>
            </div>
            <div>
              <p className={labelClass}>{t("trailers.fields.insurance")}</p>
              <p className={valueClass}>{trailer.insurance || "N/A"}</p>
            </div>
            <div>
              <p className={labelClass}>{t("trailers.fields.permit")}</p>
              <p className={valueClass}>{trailer.permit || "N/A"}</p>
            </div>
          </div>

          {/* Ubicación e Inspecciones */}
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 mt-4">
            <div>
              <p className={labelClass}>
                {t("trailers.fields.lastInspection")}
              </p>
              <p className={valueClass}>
                {trailer.inspectionDate?._seconds
                  ? getTimeAgo(
                      trailer.inspectionDate._seconds,
                      timeTranslations,
                    )
                  : "N/A"}
              </p>
            </div>
            <div className="col-span-2">
              <p className={labelClass}>{t("trailers.fields.location")}</p>
              <p className={valueClass}>{trailer.lastAddress || "N/A"}</p>
            </div>
          </div>

          {/* Próxima inspección */}
          <div
            className={`flex items-center w-full gap-2 px-4 p-3 rounded-lg ${themeClasses.cardBg} border ${themeClasses.cardBorder}`}
          >
            <i className={`ri-shield-check-line ${themeClasses.text}`}></i>
            <div className="flex w-full justify-between">
              <span className={themeClasses.textSecondary}>
                {t("trailers.fields.nextInspection")}:
              </span>
              <p className={`${themeClasses.text} font-semibold`}>
                {trailer.nextInspectionDate?._seconds
                  ? getTimeUntil(
                      trailer.nextInspectionDate._seconds,
                      undefined,
                      timeUntilTranslations,
                    )
                  : "N/A"}
              </p>
            </div>
          </div>

          {/* Detalles de Viaje */}
          <h4
            className={`text-sm font-semibold ${themeClasses.text} ${themeClasses.cardBorder} mt-4`}
          >
            {t("trailers.sections.shipmentDetails")}
          </h4>
          <div className="space-y-2">
            {trailer.shipment_details?.length === 0 ? (
              <p className={`text-sm ${themeClasses.textSecondary}`}>
                {t("trailers.modal.noShipmentDetails")}
              </p>
            ) : (
              trailer.shipment_details?.map((detail, index) => (
                <div key={index} className="flex items-center gap-2">
                  <p className={valueClass}>{detail}</p>
                </div>
              ))
            )}
          </div>
          {/* Botón ver en mapa */}
          {hasVehicle && (
            <div className="flex justify-end pt-2">
              <button
                onClick={handleViewOnMap}
                className="py-2 px-4 bg-primary-500 hover:bg-primary-400 rounded-lg text-sm font-medium transition-colors flex items-center gap-2"
              >
                <i className="ri-map-pin-line"></i>
                {t("trailers.actions.viewOnMap")}
              </button>
            </div>
          )}
        </div>
      }
    />
  );
}
